import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import dayjs from "dayjs";
import { CalendarIcon, X } from "lucide-react";

type DatePickerButtonProps = {
  date: Date | null;
  placeholder: string;
  ariaLabel: string;
  /** Omit for a date that cannot be cleared. */
  clearAriaLabel?: string;
  onDateChange: (date: Date | null) => void;
  defaultMonth?: Date;
  disabled?: boolean;
};

function formatDate(date: Date): string {
  return dayjs(date).format("DD/MM/YYYY");
}

export function DatePickerButton({
  date,
  placeholder,
  ariaLabel,
  clearAriaLabel,
  onDateChange,
  defaultMonth,
  disabled,
}: DatePickerButtonProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="min-w-[140px] justify-start gap-2"
          aria-label={ariaLabel}
          disabled={disabled}
        >
          <CalendarIcon className="text-muted-foreground h-4 w-4" />
          {date ? formatDate(date) : <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <Calendar
          mode="single"
          selected={date ?? undefined}
          defaultMonth={defaultMonth}
          disabled={disabled}
          onSelect={(d) => onDateChange(d ?? null)}
          autoFocus
          weekStartsOn={1}
        />
        {date !== null && clearAriaLabel !== undefined && (
          <div className="mt-2 flex justify-end border-t pt-2">
            <Button
              variant="ghost"
              size="sm"
              aria-label={clearAriaLabel}
              disabled={disabled}
              onClick={() => onDateChange(null)}
            >
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
